/**
 * The summon pill's self-typing example prompt (mock: "a prompt types itself
 * until you click in"). Pure + timer-injected so it is testable and the pill
 * can stop it on focus. Cadence mirrors the mock exactly: 34-74ms per typed
 * character, a 2.2s hold at the end, erase two characters per 16ms, 0.5s pause.
 */
export const EXAMPLE_PROMPTS: readonly string[] = [
  "Chase the unpaid invoices from March",
  "Turn this sheet into a one-page update and mail it to investors",
  "Book a table for four on Friday, somewhere quiet",
];

export interface TypingLoopOptions {
  prompts: readonly string[];
  onText: (text: string) => void;
  /** Injected for tests; defaults to the globals. */
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
  random?: () => number;
}

export interface TypingLoop {
  start(): void;
  stop(): void;
}

export function createTypingLoop(opts: TypingLoopOptions): TypingLoop {
  const setT = opts.setTimeout ?? ((fn, ms) => globalThis.setTimeout(fn, ms));
  const clearT =
    opts.clearTimeout ??
    ((h) => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>));
  const random = opts.random ?? Math.random;
  let prompt = 0;
  let cursor = 0;
  let dir: 1 | -1 = 1;
  let timer: unknown = null;
  let running = false;

  const schedule = (ms: number) => {
    timer = setT(tick, ms);
  };
  const tick = () => {
    if (!running) return;
    const p = opts.prompts[prompt] ?? "";
    if (dir > 0) {
      cursor += 1;
      opts.onText(p.slice(0, cursor));
      if (cursor >= p.length) {
        dir = -1;
        schedule(2200);
        return;
      }
      schedule(34 + random() * 40);
      return;
    }
    cursor -= 2;
    if (cursor <= 0) {
      cursor = 0;
      opts.onText("");
      dir = 1;
      prompt = (prompt + 1) % Math.max(1, opts.prompts.length);
      schedule(500);
      return;
    }
    opts.onText(p.slice(0, cursor));
    schedule(16);
  };

  return {
    start() {
      if (running) return;
      running = true;
      cursor = 0;
      dir = 1;
      schedule(34);
    },
    stop() {
      running = false;
      if (timer !== null) {
        clearT(timer);
        timer = null;
      }
    },
  };
}
